import { DsDialogService } from 'src/app/shared/components/ds-dialog/ds-dialog.component';
import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { DsTooltipDirective } from 'src/app/shared/components/ds-tooltip/ds-tooltip.component';
import {
  DsMenuComponent,
  DsMenuItemDirective,
  DsMenuDividerComponent,
  DsMenuTriggerDirective,
} from 'src/app/shared/components/ds-menu/ds-menu.component';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/core/auth/auth.service';
import { UserService } from 'src/app/core/user/user.service';
import { I18nService } from 'src/app/core/translation/i18n.service';
import { LanguageCode, languages } from 'src/app/core/translation/language';
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service';
import { KeyboardShortcutsService } from 'src/app/core/keyboard-shortcuts/keyboard-shortcuts.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  INotificationItem,
  NotificationCategory,
} from 'src/app/modules/notification/notification.model';
import { NotificationPanelComponent } from 'src/app/shared/components/notification-panel/notification-panel.component';
import { CommandPaletteComponent } from 'src/app/shared/components/command-palette/command-palette.component';
import { KeyboardCheatSheetComponent } from 'src/app/shared/components/keyboard-cheat-sheet/keyboard-cheat-sheet.component';
import { AppearanceService } from 'src/app/core/services/appearance.service';
import { ViewModeService } from 'src/app/core/services/view-mode.service';
import { DsAvatarComponent } from 'src/app/shared/components/ds-avatar/ds-avatar.component';
import { DsButtonComponent } from 'src/app/shared/components/ds-button/ds-button.component';
import { DsKbdComponent } from 'src/app/shared/components/ds-kbd/ds-kbd.component';
import {
  DsBreadcrumbComponent,
  DsBreadcrumbItem,
} from 'src/app/shared/components/ds-breadcrumb/ds-breadcrumb.component';
import { trKey } from 'src/app/shared/utils/translate-safe';
import { HasPermissionDirective } from 'src/app/shared/directives/has-permission/has-permission.directive';
import { FxRatesButtonComponent } from '../fx-rates/fx-rates-button.component';
import { MODULE_TITLE_MAP } from '../context-sidebar/module-sub-nav.config';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'top-navbar',
  imports: [
    RouterModule,
    DsTooltipDirective,
    DsMenuComponent,
    DsMenuItemDirective,
    DsMenuDividerComponent,
    DsMenuTriggerDirective,
    TranslateModule,
    NotificationPanelComponent,
    DsAvatarComponent,
    DsBreadcrumbComponent,
    DsButtonComponent,
    DsKbdComponent,
    FxRatesButtonComponent,
    HasPermissionDirective,
  ],
  templateUrl: './top-navbar.component.html',
  styleUrls: ['./top-navbar.component.scss'],
})
export class TopNavbarComponent implements OnInit, OnDestroy {
  // Bỏ `isScreenSmall` + `menuToggle` (2026-08-07): chúng chỉ phục vụ hamburger mở
  // mobile-drawer của shell desktop ở cỡ điện thoại — tầng đó đã gỡ, giao diện điện
  // thoại là shell riêng `/mobile`.
  private _unsubscribeAll = new Subject<void>();
  breadcrumbs: { label: string; url?: string }[] = [];

  /**
   * Bản đã DỊCH của {@link breadcrumbs}, nuôi thẳng `ds-breadcrumb`.
   *
   * `ds-breadcrumb` nhận chữ, không nhận khoá i18n — nó là primitive, cố ý không import
   * `TranslateModule` (cùng lý do với `ds-page-header.closeLabel`). Nên phép dịch phải làm
   * ở đây, và phải chạy lại khi ĐỔI NGÔN NGỮ: ống `| translate` trong template tự vẽ lại,
   * một mảng tính sẵn thì không — bỏ nhánh `onLangChange` là breadcrumb đứng nguyên tiếng
   * cũ cho tới lần điều hướng kế tiếp.
   */
  breadcrumbItems: DsBreadcrumbItem[] = [];
  notificationService = inject(NotificationService);
  private _appearanceService = inject(AppearanceService);
  private _viewModeService = inject(ViewModeService);
  notificationPanelOpen = false;
  notifications: INotificationItem[] = [];
  activeCategory: NotificationCategory | null = null;
  unreadCount = 0;
  isDarkTheme = false;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _authService: AuthService,
    private _userService: UserService,
    private _i18nService: I18nService,
    private _translateService: TranslateService,
    private _confirmationDialogService: ConfirmationDialogService,
    private _keyboardShortcutsService: KeyboardShortcutsService,
    private _dialog: DsDialogService,
    private _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => this.refreshBreadcrumbs());

    // Build initial breadcrumbs
    this.refreshBreadcrumbs();

    // Đổi ngôn ngữ ⇒ dịch lại nhãn breadcrumb (xem doc-comment `breadcrumbItems`).
    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.refreshBreadcrumbs());

    // Sync isDarkTheme from AppearanceService (already applied CSS on startup)
    this.isDarkTheme =
      this._appearanceService.current.theme === 'dark' ||
      (this._appearanceService.current.theme === 'system' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Keyboard shortcut: Cmd+K / Ctrl+K opens the command palette
    this._keyboardShortcutsService.commandPaletteOpen$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.openCommandPalette();
      });

    // Keyboard shortcut: Shift+? opens the keyboard shortcuts cheat sheet
    this._keyboardShortcutsService.cheatSheetOpen$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.openCheatSheet();
      });

    // Notification polling (SSE-first with polling fallback)
    this.notificationService.startPolling();
    this.notificationService.notifications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((n) => (this.notifications = n));
    this.notificationService.unreadCount$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((c) => (this.unreadCount = c));
    this.notificationService.activeCategory$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((c) => (this.activeCategory = c));
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  openCommandPalette(): void {
    this._dialog.open(CommandPaletteComponent, {
      // ⌘K — hộp giữa màn rộng 580 bo 12 (module-rail.jsx :: Palette, Carot ERP v2). Đây là
      // bảng lệnh DUY NHẤT của shell (cổng shell-v2.contract.spec.ts); `ds-command-palette`
      // chỉ là bản trình diễn trong Component Library.
      position: 'center',
      width: 'var(--command-palette-width, 580px)',
      panelClass: 'command-palette-dialog',
      autoFocus: false,
    });
  }

  openCheatSheet(): void {
    this._dialog.open(KeyboardCheatSheetComponent, {
      autoFocus: false,
    });
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: { label: string; url?: string }[] = [],
  ): { label: string; url?: string }[] {
    const children = route.children;
    if (children.length === 0) return breadcrumbs;

    for (const child of children) {
      const routeURL = child.snapshot.url.map((seg) => seg.path).join('/');
      if (routeURL) {
        url += `/${routeURL}`;
      }
      // `data.breadcrumb` (array of i18n keys or { label, url } objects) overrides
      // the accumulated chain — used by sections like /component-library/* where
      // the route tree is intentionally flat but the IA wants a 3-level crumb
      // (Module > Section > Item). When present, it replaces breadcrumbs entirely
      // for this navigation; the LAST item gets the current URL, earlier items
      // get an explicit url (if provided) or no url (non-navigable section).
      const breadcrumbData = child.snapshot.data['breadcrumb'];
      if (Array.isArray(breadcrumbData) && breadcrumbData.length > 0) {
        const replaced: { label: string; url?: string }[] = breadcrumbData.map(
          (entry, idx, arr) => {
            const isLast = idx === arr.length - 1;
            if (typeof entry === 'string') {
              return { label: entry, url: isLast ? url : undefined };
            }
            return {
              label: entry.label,
              url: entry.url ?? (isLast ? url : undefined),
            };
          },
        );
        return this.buildBreadcrumbs(child, url, replaced);
      }
      const title = child.snapshot.data['title'];
      if (title) {
        breadcrumbs.push({ label: title, url });
      }
      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }
    return breadcrumbs;
  }

  get currentUser(): any {
    return this._userService.userValue;
  }

  /**
   * Dựng lại chuỗi breadcrumb rồi dịch nhãn.
   *
   * Phần gập (>4 mục → đầu · … · hai mục cuối) nay do `ds-breadcrumb` lo qua `maxVisible`,
   * nên hai getter `lastTwoBreadcrumbs` / `collapsedBreadcrumbTooltip` đã bỏ.
   */
  private refreshBreadcrumbs(): void {
    this.breadcrumbs = TopNavbarComponent.withModuleRoot(
      this.buildBreadcrumbs(this._activatedRoute.root),
      this._router.url,
      (key) => trKey(this._translateService, key),
    );
    this.breadcrumbItems = this.breadcrumbs.map((crumb) => ({
      label: trKey(this._translateService, crumb.label),
      link: crumb.url,
    }));
    this._cdr.markForCheck();
  }

  /**
   * Breadcrumb gốc = TÊN MODULE (AppTopbar v2: `rootLabel` luôn là tên module, rồi mới tới các
   * mục). Phần lớn route đã tự mở đầu chuỗi bằng hằng `MODULE` của tệp routes; route chỉ khai
   * `data.title` thì không — với chúng, chèn khoá tên module từ `MODULE_TITLE_MAP` (cùng nguồn
   * với đầu sidebar). So trùng theo KHOÁ lẫn theo CHỮ ĐÃ DỊCH, vì vài route đặt khoá khác
   * nhưng cùng nghĩa — chèn mù sẽ ra "Quản lý bán hàng › Quản lý bán hàng".
   * Nhãn vẫn là khoá i18n (CLAUDE.md §5.5) — dịch ở `refreshBreadcrumbs` như mọi mục khác.
   * Static để spec gọi thẳng không cần TestBed.
   */
  static withModuleRoot(
    crumbs: { label: string; url?: string }[],
    url: string,
    translate: (key: string) => string,
  ): { label: string; url?: string }[] {
    const segment = url.split('?')[0].split('#')[0].split('/').filter(Boolean)[0];
    if (!segment) return crumbs;
    const base = `/${segment}`;
    const rootKey = MODULE_TITLE_MAP[base];
    if (!rootKey) return crumbs;
    const first = crumbs[0];
    const norm = (s: string) => s.trim().toLocaleLowerCase();
    if (
      first &&
      (first.label === rootKey || norm(translate(first.label)) === norm(translate(rootKey)))
    ) {
      return crumbs;
    }
    return [{ label: rootKey, url: base }, ...crumbs];
  }

  /**
   * Nhãn phím tắt trên ô tìm — `⌘K` trên macOS, `Ctrl K` ở nơi khác.
   *
   * `KeyboardShortcutsService` nhận CẢ `metaKey` lẫn `ctrlKey`, nên cắm cứng `⌘K` là nói
   * sai với đa số người dùng của kho này (máy Windows).
   */
  get commandPaletteHint(): string {
    return this.isApplePlatform ? '⌘K' : 'Ctrl K';
  }

  /** `aria-keyshortcuts` dùng cú pháp chuẩn WAI-ARIA, khác chuỗi hiển thị ở trên. */
  get commandPaletteAriaShortcut(): string {
    return this.isApplePlatform ? 'Meta+K' : 'Control+K';
  }

  private get isApplePlatform(): boolean {
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  }

  get currentLanguageLabel(): string {
    const lang = languages.find((l) => l.value === this._i18nService.language);
    return lang ? lang.label : 'English';
  }

  toggleTheme(): void {
    const current = this._appearanceService.current.theme;
    const newTheme = current === 'dark' ? 'light' : 'dark';
    this._appearanceService.patch('theme', newTheme);
    this.isDarkTheme = newTheme === 'dark';
  }

  toggleLanguage(): void {
    const currentLang = this._i18nService.language;
    const newLang =
      currentLang === LanguageCode.English ? LanguageCode.Vietnamese : LanguageCode.English;
    this._i18nService.language = newLang;
  }

  /** Chuyển sang giao diện mobile — ghi nhớ lựa chọn, quay lại qua "Chuyển sang bản desktop" trong /mobile. */
  switchToMobileView(): void {
    this._viewModeService.switchTo('mobile');
  }

  logout(): void {
    const confirmRef = this._confirmationDialogService.open({
      message: this._translateService.instant('Services.LogoutConfirm'),
      title: this._translateService.instant('Button.Logout'),
      actions: {
        cancel: {
          show: true,
          label: this._translateService.instant('Button.Cancel'),
        },
        confirm: {
          show: true,
          label: this._translateService.instant('Button.Confirm'),
          color: 'accent',
        },
      },
    });

    confirmRef.afterClosed().subscribe((type: string) => {
      if (type === 'confirmed') {
        this._authService.logout().subscribe();
      }
    });
  }
}
